import React, { useState } from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Reveal } from "@/hooks/useScrollReveal";
import { formatPrice, FUEL_TYPES, BODY_TYPES } from "@/lib/format";
import { Sparkles, Loader2, Eye } from "lucide-react";
import api from "@/lib/api";
import { useSiteContent } from "@/context/SiteContentContext";
import { DEFAULT_SITE_CONTENT } from "@/lib/defaultSiteContent";
import { SITE_CONTAINER } from "@/lib/siteLayout";

export const AIRecommender = ({ onQuickView }) => {
  const { content } = useSiteContent();
  const ai = content.ai_recommender || DEFAULT_SITE_CONTENT.ai_recommender;
  const budgets = (ai.budgets || []).map((b) => ({ v: b.value, l: b.label }));
  const [budget, setBudget] = useState("");
  const [fuel, setFuel] = useState("");
  const [body, setBody] = useState("");
  const [usage, setUsage] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const submit = async () => {
    setLoading(true);
    setError("");
    setResult(null);
    try {
      const res = await api.post("/ai/recommend", {
        budget_max: budget ? Number(budget) : null,
        fuel: fuel || null,
        body_type: body || null,
        usage,
      });
      setResult(res.data);
    } catch (e) {
      setError(ai.error_message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="ai" data-testid="ai-section" className="relative py-24">
      <div className={SITE_CONTAINER}>
        <Reveal>
          <div className="glass rounded-3xl p-8 md:p-12 relative overflow-hidden border border-electric/30 blue-glow">
            <div className="absolute -top-24 -right-24 w-64 h-64 bg-electric/20 rounded-full blur-3xl pointer-events-none" />

            <div className="relative">
              <div className="flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-electric mb-3">
                <Sparkles className="w-4 h-4" strokeWidth={1.5} /> {ai.eyebrow}
              </div>
              <h2 className="font-heading text-3xl md:text-4xl font-light tracking-tight">
                {ai.title}
              </h2>
              <p className="text-slate-400 mt-3 max-w-xl">
                {ai.description}
              </p>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-8">
                <AISelect testid="ai-budget" label={ai.budget_label} value={budget} onChange={setBudget}
                  placeholder={ai.budget_placeholder} options={budgets} />
                <AISelect testid="ai-fuel" label={ai.fuel_label} value={fuel} onChange={setFuel}
                  placeholder={ai.fuel_placeholder} options={FUEL_TYPES.map((f) => ({ v: f, l: f }))} />
                <AISelect testid="ai-body" label={ai.body_label} value={body} onChange={setBody}
                  placeholder={ai.body_placeholder} options={BODY_TYPES.map((b) => ({ v: b, l: b }))} />
              </div>

              <div className="mt-4">
                <label className="text-xs tracking-[0.15em] uppercase text-slate-400 block mb-2">
                  {ai.usage_label}
                </label>
                <textarea
                  data-testid="ai-usage"
                  value={usage}
                  onChange={(e) => setUsage(e.target.value)}
                  rows={2}
                  placeholder={ai.usage_placeholder}
                  className="w-full bg-white/[0.03] border border-white/10 rounded-xl p-3 text-sm text-white placeholder:text-slate-500 focus:border-electric outline-none transition-colors resize-none"
                />
              </div>

              <button
                data-testid="ai-submit"
                onClick={submit}
                disabled={loading}
                className="mt-6 inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-electric hover:bg-electric-light disabled:opacity-50 text-white font-medium transition-all hover:blue-glow"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" strokeWidth={1.5} />}
                {loading ? ai.submit_loading_label : ai.submit_label}
              </button>

              {error && <p data-testid="ai-error" className="mt-4 text-sm text-red-400">{error}</p>}

              {result && (
                <div data-testid="ai-result" className="mt-8 space-y-4">
                  {result.intro && <p className="text-slate-200 leading-relaxed">{result.intro}</p>}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {result.recommendations.map(({ car, reason }) => (
                      <div key={car.id} data-testid={`ai-rec-${car.id}`} className="glass-strong rounded-2xl overflow-hidden">
                        <div className="aspect-[16/9] overflow-hidden">
                          <img src={car.image} alt={car.model} className="w-full h-full object-cover" />
                        </div>
                        <div className="p-4">
                          <div className="flex items-center justify-between">
                            <div>
                              <div className="text-xs text-slate-400">{car.make}</div>
                              <div className="font-heading text-lg font-medium">{car.model}</div>
                            </div>
                            <div className="text-electric font-medium">{formatPrice(car.price)}</div>
                          </div>
                          <p className="text-sm text-slate-300 mt-2 leading-relaxed">{reason}</p>
                          <button
                            onClick={() => onQuickView(car)}
                            className="mt-3 inline-flex items-center gap-1.5 text-sm text-electric hover:text-electric-light"
                          >
                            <Eye className="w-4 h-4" strokeWidth={1.5} /> {ai.quick_view_label}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                  {result.recommendations.length === 0 && (
                    <p className="text-slate-400 text-sm">{ai.no_results_message}</p>
                  )}
                </div>
              )}
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

const AISelect = ({ testid, label, value, onChange, placeholder, options }) => (
  <div>
    <label className="text-xs tracking-[0.15em] uppercase text-slate-400 block mb-2">{label}</label>
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={testid} className="bg-white/[0.03] border-white/10 text-white h-11">
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent className="bg-anthracite-700 border-white/10 text-white">
        {options.map((o) => (
          <SelectItem key={o.v} value={o.v} className="focus:bg-electric focus:text-white">
            {o.l}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  </div>
);
